import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BlocksContainer, FluidContainer, Heading, WallpaperWrapper } from '../Components/_Common/common.styles';
import BackButton from '../Components/BackButton/BackButton';
import { useCategories } from '../Hooks/useCategories';
import { usePosts } from '../Hooks/usePosts';
import { useRecentPosts } from '../Hooks/useRecentPosts';
import CategoryFilter from '../Components/CategoryFilter/CategoryFilter';
import BlogPostCard from '../Components/BlogPostCard/BlogPostCard';
import RecentPostsSidebar from '../Components/RecentPostsSidebar/RecentPostsSidebar';
import Loader from '../Components/Loader/Loader';
import { Helmet } from 'react-helmet-async';

const BlogsPage = () => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;

    const { posts, loading: postsLoading, error: postsError } = usePosts(currentLang);
    const { categories, loading: categoriesLoading, error: categoriesError } = useCategories(currentLang);
    const { recentPosts, loading: recentPostsLoading, error: recentPostsError } = useRecentPosts(currentLang);

    const [selectedCategory, setSelectedCategory] = useState(currentLang === 'en' ? 'All' : 'Tous'); // Default value based on language

    const filteredPosts = selectedCategory === (currentLang === 'en' ? 'All' : 'Tous')
        ? posts
        : posts.filter(post => post.categories.some(cat => cat.title === selectedCategory));

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(currentLang, options);
    };

    if (postsLoading || categoriesLoading || recentPostsLoading) {
        return <FluidContainer><Loader /></FluidContainer>;
    }

    if (postsError || categoriesError || recentPostsError) {
        return <FluidContainer><Heading>{t('errorLoadingContent')}</Heading></FluidContainer>;
    }

    if (posts.length === 0) {
        return <FluidContainer><Heading>{t('blogs.noPosts')}</Heading></FluidContainer>;
    }

    const schemaOrgBlogPage = {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "ontheweb.agency Blog",
        "description": "Expert insights on web design, development trends, and best practices",
        "url": "https://ontheweb.agency/blogs",
        "publisher": {
            "@type": "Organization",
            "name": "ontheweb.agency",
            "logo": {
                "@type": "ImageObject",
                "url": "https://ontheweb.agency/logo.png"
            }
        },
        "blogPost": filteredPosts.map(post => ({
            "@type": "BlogPosting",
            "headline": post.title,
            "datePublished": post.publishedAt,
            "author": {
                "@type": "Person",
                "name": post.author?.name || "Matan Dessaur"
            },
            "url": `https://ontheweb.agency/blog/${post.slug}`
        }))
    };

    return (
        <WallpaperWrapper>
            <Helmet>
                <html lang={currentLang} />
                <title>OnTheWeb Blogs | Web Design & Development Blog</title>
                <meta name="description" content="Get expert insights on web design, development trends, and digital marketing strategies. Our blog helps businesses optimize their online presence and grow." />
                <meta property="og:title" content="Web Design & Development Blog | OnTheWeb Agency Insights" />
                <meta property="og:description" content="Get expert insights on web design, development trends, and digital marketing strategies. Our blog helps businesses optimize their online presence and grow." />
                <meta property="og:url" content="https://ontheweb.agency/blogs" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://ontheweb.agency/share-image.jpg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="900" />
                <meta property="og:image:type" content="image/jpeg" />
                <meta property="og:image:alt" content="ontheweb.agency Blog - Web Design and Development Insights" />
                <meta property="og:site_name" content="ontheweb.agency" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@onthewebagency" />
                <meta name="twitter:title" content="Web Design & Development Blog | OnTheWeb Agency Insights" />
                <meta name="twitter:description" content="Get expert insights on web design, development trends, and digital marketing strategies. Our blog helps businesses optimize their online presence and grow." />
                <meta name="twitter:image" content="https://ontheweb.agency/share-image.jpg" />
                <meta name="twitter:image:alt" content="ontheweb.agency Blog - Web Design and Development Insights" />
                {/* Single canonical URL - site uses browser detection for language */}
                <link rel="canonical" href="https://ontheweb.agency/blogs" />
                
                <script type="application/ld+json">
                    {JSON.stringify(schemaOrgBlogPage)}
                </script>
            </Helmet>
            <BlocksContainer>
                <BlogContentWrapper>
                    <MainSection>
                        <CategoryFilter
                            categories={categories}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                        />
                        <BlogGrid>
                            {filteredPosts.map(post => (
                                <BlogPostCard key={post._id} post={post} formatDate={formatDate} />
                            ))}
                            {filteredPosts.length === 0 && (
                                <ComingSoonBlogCard>
                                    <Heading>{t('blogs.comingSoon')} . . .</Heading>
                                </ComingSoonBlogCard>
                            )}
                        </BlogGrid>
                    </MainSection>
                    <RecentPostsSidebar recentPosts={recentPosts} formatDate={formatDate} />
                </BlogContentWrapper>
                <BackButton link={""} />
            </BlocksContainer>
        </WallpaperWrapper>
    );
};

const ComingSoonBlogCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  transition: all 0.3s ease;
  min-height: 420px;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 20px rgba(0,0,0,0.1);
  }

  h2 {
    width: 100%;
    font-weight: 300;
    margin: 0;
    text-align: center;
    color: #2d3748;
    font-size: 1.8rem;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    min-height: 300px;
  }
`;

const BlogContentWrapper = styled.div`
  display: flex;
  gap: 2.5rem;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0;
  }
`;

const MainSection = styled.div`
  flex: 3;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  margin-top: 1.5rem;

  @media (max-width: 1440px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 1.5rem;
  }
`;

export default BlogsPage;
